import React, { useState, useEffect, useCallback, useMemo } from 'react';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './SpecimenMasterPage.css';

const PRESET_SWATCHES = [
  { name: 'Lavender / Purple (EDTA)', color: '#9333ea' },
  { name: 'Red / Plain (Clot Activator)', color: '#dc2626' },
  { name: 'Gold / SST (Gel Separator)', color: '#eab308' },
  { name: 'Light Blue (Sodium Citrate)', color: '#0ea5e9' },
  { name: 'Green (Lithium Heparin)', color: '#16a34a' },
  { name: 'Grey (Sodium Fluoride)', color: '#64748b' },
  { name: 'Yellow (ACD Solution)', color: '#facc15' },
  { name: 'Black (ESR Sodium Citrate)', color: '#1e293b' },
  { name: 'Sterile Cup (Urine / Body Fluids)', color: '#f97316' }
];

export const SpecimenMasterPage = ({ showToast }) => {
  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [subType, setSubType] = useState('specimen'); // 'specimen' | 'container'
  const [isEditMode, setIsEditMode] = useState(false);

  // Form States
  const [specimenForm, setSpecimenForm] = useState({
    sampleTypeId: 0,
    sampleName: '',
    displayName: '',
    color: '#9333ea',
    colorName: 'Lavender',
    isActive: true
  });

  const [containerForm, setContainerForm] = useState({
    containerId: 0,
    containerName: '',
    color: '#dc2626',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Catalogs
  const [specimens, setSpecimens] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Confirmation
  const [confirmTarget, setConfirmTarget] = useState(null); // { type, item }
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Load All Catalogs
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [specRes, contRes] = await Promise.all([
        masterService.getSampleTypes(),
        masterService.getSampleContainers()
      ]);
      if (specRes.success && specRes.data) setSpecimens(specRes.data || []);
      if (contRes.success && contRes.data) setContainers(contRes.data || []);
    } catch (err) {
      showToast?.('Error loading specimen catalogs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Switch to clean Form
  const handleAddNew = (type = subType) => {
    setSubType(type);
    if (type === 'specimen') {
      setSpecimenForm({
        sampleTypeId: 0,
        sampleName: '',
        displayName: '',
        color: '#9333ea',
        colorName: 'Lavender',
        isActive: true
      });
    } else {
      setContainerForm({
        containerId: 0,
        containerName: '',
        color: '#dc2626',
        isActive: true
      });
    }
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Switch to Edit Form
  const handleEditSpecimen = (row) => {
    setSubType('specimen');
    setSpecimenForm({
      sampleTypeId: row.sampleTypeId,
      sampleName: row.sampleName || '',
      displayName: row.displayName || row.sampleName || '',
      color: row.color || '#9333ea',
      colorName: row.colorName || '',
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  const handleEditContainer = (row) => {
    setSubType('container');
    setContainerForm({
      containerId: row.containerId,
      containerName: row.containerName || '',
      color: row.color || '#dc2626',
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};

    if (subType === 'specimen') {
      if (!specimenForm.sampleName.trim()) errs.sampleName = 'Specimen Name is required.';
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }

      setSaving(true);
      try {
        const payload = {
          sampleName: specimenForm.sampleName.trim(),
          displayName: specimenForm.displayName.trim() || specimenForm.sampleName.trim(),
          color: specimenForm.color || '#9333ea',
          colorName: specimenForm.colorName.trim() || null,
          isActive: specimenForm.isActive
        };

        let res;
        if (isEditMode) {
          res = await masterService.updateSampleType(specimenForm.sampleTypeId, payload);
        } else {
          res = await masterService.createSampleType(payload);
        }

        if (res.success) {
          showToast?.(`Specimen matrix ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
          setViewMode('list');
          fetchAll();
        } else {
          showToast?.(res.message || 'Operation failed.', 'error');
        }
      } catch (err) {
        showToast?.('Error saving specimen matrix.', 'error');
      } finally {
        setSaving(false);
      }
    } else {
      if (!containerForm.containerName.trim()) errs.containerName = 'Container Name is required.';
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }

      setSaving(true);
      try {
        const payload = {
          containerName: containerForm.containerName.trim(),
          color: containerForm.color || '#dc2626',
          isActive: containerForm.isActive
        };

        let res;
        if (isEditMode) {
          res = await masterService.updateSampleContainer(containerForm.containerId, payload);
        } else {
          res = await masterService.createSampleContainer(payload);
        }

        if (res.success) {
          showToast?.(`Container tube ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
          setViewMode('list');
          fetchAll();
        } else {
          showToast?.(res.message || 'Operation failed.', 'error');
        }
      } catch (err) {
        showToast?.('Error saving container tube.', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const { type, item } = confirmTarget;
      const newStatus = !item.isActive;

      if (type === 'specimen') {
        const res = await masterService.updateSampleTypeStatus(item.sampleTypeId, newStatus);
        if (res.success) {
          showToast?.(`Specimen ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
          setSpecimens(prev => prev.map(s => s.sampleTypeId === item.sampleTypeId ? { ...s, isActive: newStatus } : s));
        }
      } else {
        const res = await masterService.updateSampleContainerStatus(item.containerId, newStatus);
        if (res.success) {
          showToast?.(`Container ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
          setContainers(prev => prev.map(c => c.containerId === item.containerId ? { ...c, isActive: newStatus } : c));
        }
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Filtered lists
  const filteredSpecimens = useMemo(() => {
    return specimens.filter(s => {
      const matchSearch = !searchQuery || s.sampleName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === 'all' ? true : statusFilter === 'active' ? s.isActive : !s.isActive;
      return matchSearch && matchStatus;
    });
  }, [specimens, searchQuery, statusFilter]);

  const filteredContainers = useMemo(() => {
    return containers.filter(c => {
      const matchSearch = !searchQuery || c.containerName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === 'all' ? true : statusFilter === 'active' ? c.isActive : !c.isActive;
      return matchSearch && matchStatus;
    });
  }, [containers, searchQuery, statusFilter]);

  // Centralized Standardized Pagination
  const specimenPagination = usePagination({
    items: filteredSpecimens,
    resetDeps: [searchQuery, statusFilter]
  });

  const containerPagination = usePagination({
    items: filteredContainers,
    resetDeps: [searchQuery, statusFilter]
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? `Update ${subType === 'specimen' ? 'Specimen' : 'Container'}` : `Create ${subType === 'specimen' ? 'Specimen Matrix' : 'Collection Tube'}`) : 'Specimen & Tube Master'}
      masterName={subType === 'specimen' ? 'Specimen' : 'Container'}
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew(subType);
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          {/* Subtype Toggle */}
          {!isEditMode && (
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
              <button
                type="button"
                className={`btn ${subType === 'specimen' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSubType('specimen')}
              >
                Specimen Matrix Form
              </button>
              <button
                type="button"
                className={`btn ${subType === 'container' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSubType('container')}
              >
                Collection Tube (Container) Form
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {subType === 'specimen' ? (
              <FormSection
                title="01. Specimen Matrix Classification"
                iconColor="#4287f5"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                }
              >
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Specimen Name <span className="form-req">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.sampleName ? 'border-danger' : ''}`}
                      value={specimenForm.sampleName}
                      onChange={(e) => {
                        setSpecimenForm({ ...specimenForm, sampleName: e.target.value });
                        if (errors.sampleName) setErrors({ ...errors, sampleName: null });
                      }}
                      placeholder="e.g. Whole Blood, Serum, Plasma, Urine"
                      required
                    />
                    {errors.sampleName && <span className="error-message">{errors.sampleName}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Display Name / Alias</label>
                    <input
                      type="text"
                      className="form-input"
                      value={specimenForm.displayName}
                      onChange={(e) => setSpecimenForm({ ...specimenForm, displayName: e.target.value })}
                      placeholder="Printed on collection labels"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Color Code Tag</label>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={specimenForm.color || '#9333ea'}
                        onChange={(e) => setSpecimenForm({ ...specimenForm, color: e.target.value })}
                        style={{ width: '42px', height: '38px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={specimenForm.colorName}
                        onChange={(e) => setSpecimenForm({ ...specimenForm, colorName: e.target.value })}
                        placeholder="Color Name (e.g. Purple)"
                      />
                    </div>
                  </div>

                  <div className="form-group col-span-2">
                    <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={specimenForm.isActive}
                        onChange={(e) => setSpecimenForm({ ...specimenForm, isActive: e.target.checked })}
                      />
                      <span><strong>Specimen Active</strong> (Available during test configuration and sample registration)</span>
                    </label>
                  </div>
                </div>
              </FormSection>
            ) : (
              <FormSection
                title="01. Collection Tube / Container Information"
                iconColor="#10b981"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2" />
                    <path d="M8.5 2h7" />
                    <path d="M14.5 16h-5" />
                  </svg>
                }
              >
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Container / Tube Name <span className="form-req">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.containerName ? 'border-danger' : ''}`}
                      value={containerForm.containerName}
                      onChange={(e) => {
                        setContainerForm({ ...containerForm, containerName: e.target.value });
                        if (errors.containerName) setErrors({ ...errors, containerName: null });
                      }}
                      placeholder="e.g. EDTA Tube, SST Gel Separator, Plain Red"
                      required
                    />
                    {errors.containerName && <span className="error-message">{errors.containerName}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cap Color Code</label>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={containerForm.color || '#dc2626'}
                        onChange={(e) => setContainerForm({ ...containerForm, color: e.target.value })}
                        style={{ width: '42px', height: '38px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={containerForm.color}
                        onChange={(e) => setContainerForm({ ...containerForm, color: e.target.value })}
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>

                  <div className="form-group col-span-2">
                    <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={containerForm.isActive}
                        onChange={(e) => setContainerForm({ ...containerForm, isActive: e.target.checked })}
                      />
                      <span><strong>Container Active</strong> (Available for phlebotomy barcoding and specimen accessioning)</span>
                    </label>
                  </div>
                </div>
              </FormSection>
            )}

            {/* Presets swatch bar */}
            <div style={{ marginTop: '1rem', marginBottom: '2rem', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>
                Quick Preset Tube Colors:
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {PRESET_SWATCHES.map((swatch) => (
                  <button
                    key={swatch.color}
                    type="button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--card-bg)',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                    onClick={() => {
                      if (subType === 'specimen') {
                        setSpecimenForm(prev => ({ ...prev, color: swatch.color, colorName: swatch.name.split(' ')[0] }));
                      } else {
                        setContainerForm(prev => ({ ...prev, color: swatch.color }));
                      }
                    }}
                  >
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: swatch.color }} />
                    <span>{swatch.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '140px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="specimen-master-container">
          {/* Tabs Navigation Bar */}
          <div className="specimen-tabs-bar">
            <button
              type="button"
              className={`specimen-tab-item ${subType === 'specimen' ? 'active' : ''}`}
              onClick={() => {
                setSubType('specimen');
                setSearchQuery('');
              }}
            >
              <span className="tab-icon">🧪</span>
              <span>Biological Specimen Matrices</span>
              <span className="specimen-tab-badge">{filteredSpecimens.length}</span>
            </button>
            <button
              type="button"
              className={`specimen-tab-item ${subType === 'container' ? 'active' : ''}`}
              onClick={() => {
                setSubType('container');
                setSearchQuery('');
              }}
            >
              <span className="tab-icon">🩸</span>
              <span>Collection Tubes & Containers</span>
              <span className="specimen-tab-badge">{filteredContainers.length}</span>
            </button>
          </div>

          <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
            {/* Filter Bar */}
            <div className="filter-bar">
              <div className="filter-bar-left">
                <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                  <input
                    type="text"
                    className="form-input"
                    style={{ paddingLeft: '2.4rem' }}
                    placeholder={subType === 'specimen' ? 'Search biological specimen matrices...' : 'Search collection tubes & containers...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>

                <select
                  className="form-select"
                  style={{ width: '150px' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              <div className="filter-bar-right" style={{ display: 'flex', gap: '0.6rem' }}>
                {subType === 'specimen' ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleAddNew('specimen')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    + Add Specimen Matrix
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleAddNew('container')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    + Add Tube Container
                  </button>
                )}
              </div>
            </div>

            {/* Subtype Tab Table View */}
            {subType === 'specimen' ? (
              <div>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Specimen Matrices Catalog ({filteredSpecimens.length})
                </h3>
                <div className="table-responsive">
                  <table className="master-table mvc-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>Sr.</th>
                        <th style={{ minWidth: '240px' }}>Specimen Sample Name</th>
                        <th style={{ width: '220px', minWidth: '180px' }}>Display Alias</th>
                        <th style={{ width: '180px', minWidth: '150px' }}>Color Tag</th>
                        <th style={{ width: '110px', minWidth: '100px', textAlign: 'center' }}>Status</th>
                        <th style={{ width: '150px', minWidth: '140px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {specimenPagination.pagedItems.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                            No specimens matching search.
                          </td>
                        </tr>
                      ) : (
                        specimenPagination.pagedItems.map((r, index) => {
                          const sNo = (specimenPagination.page - 1) * specimenPagination.pageSize + index + 1;
                          return (
                            <tr key={r.sampleTypeId || index}>
                              <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                                {sNo}
                              </td>
                              <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                {r.sampleName}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>
                                {r.displayName || r.sampleName}
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: r.color || '#9333ea', border: '1px solid rgba(0,0,0,0.1)' }} />
                                  <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.colorName || r.color}</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                                  {r.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11.5px' }}
                                    onClick={() => handleEditSpecimen(r)}
                                    title="Edit Specimen"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11.5px', color: r.isActive ? '#ef4444' : '#10b981' }}
                                    onClick={() => setConfirmTarget({ type: 'specimen', item: r })}
                                  >
                                    {r.isActive ? 'Deactivate' : 'Activate'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Standardized LIMS Centralized Pagination */}
                <CommonPagination
                  page={specimenPagination.page}
                  pageSize={specimenPagination.pageSize}
                  totalRecords={specimenPagination.totalRecords}
                  onPageChange={specimenPagination.setPage}
                  onPageSizeChange={specimenPagination.setPageSize}
                  loading={loading}
                />
              </div>
            ) : (
              <div>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Collection Tubes & Containers Catalog ({filteredContainers.length})
                </h3>
                <div className="table-responsive">
                  <table className="master-table mvc-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>Sr.</th>
                        <th style={{ minWidth: '280px' }}>Container / Tube Name</th>
                        <th style={{ width: '220px', minWidth: '180px' }}>Cap Color Code</th>
                        <th style={{ width: '110px', minWidth: '100px', textAlign: 'center' }}>Status</th>
                        <th style={{ width: '150px', minWidth: '140px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {containerPagination.pagedItems.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                            No containers matching search.
                          </td>
                        </tr>
                      ) : (
                        containerPagination.pagedItems.map((r, index) => {
                          const sNo = (containerPagination.page - 1) * containerPagination.pageSize + index + 1;
                          return (
                            <tr key={r.containerId || index}>
                              <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                                {sNo}
                              </td>
                              <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                {r.containerName}
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: r.color || '#dc2626', border: '1px solid rgba(0,0,0,0.1)' }} />
                                  <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.color}</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                                  {r.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11.5px' }}
                                    onClick={() => handleEditContainer(r)}
                                    title="Edit Container"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11.5px', color: r.isActive ? '#ef4444' : '#10b981' }}
                                    onClick={() => setConfirmTarget({ type: 'container', item: r })}
                                  >
                                    {r.isActive ? 'Deactivate' : 'Activate'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Standardized LIMS Centralized Pagination */}
                <CommonPagination
                  page={containerPagination.page}
                  pageSize={containerPagination.pageSize}
                  totalRecords={containerPagination.totalRecords}
                  onPageChange={containerPagination.setPage}
                  onPageSizeChange={containerPagination.setPageSize}
                  loading={loading}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.item?.isActive ? `Deactivate ${confirmTarget?.type === 'specimen' ? 'Specimen' : 'Container'}` : `Activate ${confirmTarget?.type === 'specimen' ? 'Specimen' : 'Container'}`}
        message={
          confirmTarget?.item?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.type === 'specimen' ? confirmTarget?.item?.sampleName : confirmTarget?.item?.containerName}"?`
            : `Are you sure you want to activate "${confirmTarget?.type === 'specimen' ? confirmTarget?.item?.sampleName : confirmTarget?.item?.containerName}"?`
        }
        confirmLabel={confirmTarget?.item?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.item?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default SpecimenMasterPage;
